import { Card } from 'antd';
import { css, cx } from 'emotion';
import theme from 'config/theme';
import { PropertyType, PropertyDetailsProps } from 'waypoint-types';
import { formatNumberToDecimalPlace, formatNumber } from 'utils/formatters';
import { useEffect, useState } from 'react';

interface PropertyCardProps {
    property: PropertyType;
    selected: boolean;
    showPhoto: boolean;
    onPropertySelected?: (value: PropertyType) => void;
    leasesData?: PropertyDetailsProps[];
}

const cardDescriptionStyle = css`
    p {
        margin-bottom: 0px;
    }
`;

const compactCardHeaderStyle = css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const coverStyle = css`
    overflow: 'hidden';
    height: 0;
    padding-bottom: 56.25%;
    background-position: center center;
    background-size: cover;
    background-repeat: no-repeat;
`;

const propertyCardHeaderContent = css`
    flex: 3 1;
    max-width: fit-content;
    font-size: 16px;
    font-weight: bold;
`;

const selectedPropertyCardHeader = css`
    color: ${theme.colors.blues.primary};
`;

const cardStyle = css`
    overflow: hidden;
    border-bottom: 3px solid ${theme.colors.blues.antBlue} !important;
    margin-top: 12px !important;
    position: relative !important;
    cursor: pointer !important;
    &:hover {
        box-shadow:
            rgb(14 30 37 / 12%) 0px 2px 4px 0px,
            rgb(27 62 78 / 38%) 0px 2px 16px 0px !important;
    }
`;

const fallbackImageURI =
    'data:image/png;base64,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';

const CoverPhoto = ({ url }: { url: string }): JSX.Element => {
    return (
        <div
            className={coverStyle}
            style={{
                backgroundImage: `url(${url}), url(${fallbackImageURI})`,
            }}
        />
    );
};

const PropertyCard = ({
    property,
    selected,
    showPhoto = false,
    onPropertySelected,
    leasesData,
}: PropertyCardProps): JSX.Element => {
    const [photoTimestamp, setPhotoTimestamp] = useState<number | undefined>();

    const leasePropertyData = leasesData?.find(
        (e) => e.entity_code === property.id
    );

    useEffect(() => {
        setPhotoTimestamp(new Date().getTime());
    }, []);

    const propertyTotalUnits = leasePropertyData?.total_units
        ? formatNumber(leasePropertyData?.total_units) + ' Units'
        : '';
    const propertySquareFootage = leasePropertyData?.rentable_sq_ft
        ? formatNumberToDecimalPlace(leasePropertyData?.rentable_sq_ft, 2) +
          ' RSF '
        : '';
    return (
        <Card
            key={`property-card-${property.id}`}
            data-property-id={property.id}
            hoverable
            className={cardStyle}
            bodyStyle={{ padding: '8px' }}
            cover={
                showPhoto && (
                    <CoverPhoto
                        url={`${property.entityPhoto}?${photoTimestamp}`}
                    />
                )
            }
            onClickCapture={() =>
                onPropertySelected && onPropertySelected(property)
            }
            size={'small'}
        >
            <div className={compactCardHeaderStyle}>
                <div
                    className={cx(
                        propertyCardHeaderContent,
                        selected && selectedPropertyCardHeader
                    )}
                >
                    {property.display_name} ({property.display_code})
                </div>
            </div>
            <div className={cardDescriptionStyle}>
                <p>{property.asset_type_name}</p>
                <p>
                    {propertySquareFootage} {propertySquareFootage && '|'}{' '}
                    {propertyTotalUnits}
                </p>
                <p>
                    {[
                        property.street_address,
                        property.city,
                        property.state_abbr,
                        property.postal_code,
                    ]
                        .filter((x) => x?.trim().length)
                        .join(', ')}
                </p>
            </div>
        </Card>
    );
};

export default PropertyCard;
